/* import __COLOCATED_TEMPLATE__ from './create-order-modal.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
/* === ⚠️ THIS FILE CURRENTLY USES DEPRECATED PATTERNS ⚠️ === */
/* === 🔗 For more information visit https://go.inter.com/ember-best-practices 🔗 */
/* === 🚀 Please consider refactoring & removing some of the comments below when working on this file 🚀 */
/* eslint-disable @intercom/intercom/no-bare-strings */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import ajax from 'embercom/lib/ajax';
import { action } from '@ember/object';
import ENV from 'embercom/config/environment';
import ShopifySavedDraftOrder from 'embercom/components/notifications/shopify-saved-draft-order';

class ProductVariant {
  @tracked display_name;
  @tracked image_src;
  @tracked price;
  @tracked sku;
  @tracked variant_id;

  constructor(display_name, image_src, price, sku, variant_id) {
    this.display_name = display_name;
    this.image_src = image_src;
    this.price = price;
    this.sku = sku;
    this.variant_id = variant_id;
  }

  static fromHash(hash) {
    return new ProductVariant(
      hash.display_name,
      hash.image_src,
      hash.price,
      hash.sku,
      hash.variant_id,
    );
  }
}

class SearchSuggestion {
  @tracked productVariant;
  @tracked isHighlighted;

  constructor(productVariant, isHighlighted = false) {
    this.productVariant = productVariant;
    this.isHighlighted = isHighlighted;
  }

  static fromProduct(productHash) {
    return new SearchSuggestion(ProductVariant.fromHash(productHash));
  }

  get name() {
    return this.productVariant.display_name;
  }
}

class LineItem {
  @tracked productVariant;
  @tracked quantity;

  constructor(productVariant, quantity) {
    this.productVariant = productVariant;
    this.quantity = quantity;
  }

  static fromProductVariant(productVariant, quantity = 1) {
    return new LineItem(productVariant, quantity);
  }

  incrementQuantity() {
    this.quantity++;
  }
}

class OrderDiscount {
  @tracked amount;
  @tracked type;
  @tracked reason;
  @tracked currencySymbol;

  constructor(amount, type, reason, currencySymbol) {
    this.amount = amount;
    this.type = type;
    this.reason = reason;
    this.currencySymbol = currencySymbol;
  }

  get formattedAmount() {
    if (this.amount) {
      if (this.type === 'fixed_amount') {
        return `-${this.currencySymbol}${Number.parseFloat(this.amount).toFixed(2)}`;
      }
      return `-%${Number.parseFloat(this.amount).toFixed(2)}`;
    }
  }
}

class OrderShipping {
  @tracked type;
  @tracked amount;
  @tracked name;
  @tracked currencySymbol;

  constructor(type, amount, name, currencySymbol) {
    this.type = type;
    this.amount = amount;
    this.name = name;
    this.currencySymbol = currencySymbol;
  }

  get formattedAmount() {
    if (this.type === 'free') {
      return 'Free';
    } else if (this.amount) {
      return this.currencySymbol + Number.parseFloat(this.amount).toFixed(2);
    }
  }
}

class Order {
  @tracked lineItems = [];
  @tracked discount;
  @tracked shipping;

  onSearchSuggestionSelected(searchSuggestion) {
    let lineItem = this.lineItems.find(
      (lineItem) =>
        lineItem.productVariant.variant_id === searchSuggestion.productVariant.variant_id,
    );
    if (lineItem) {
      lineItem.incrementQuantity();
    } else {
      this.lineItems.push(LineItem.fromProductVariant(searchSuggestion.productVariant, 1));
    }
    this.lineItems = this.lineItems;
  }

  toLineItemsParams() {
    return this.lineItems.map((lineItem) => {
      return {
        variant_id: lineItem.productVariant.variant_id,
        quantity: lineItem.quantity,
      };
    });
  }

  toDiscountParams() {
    return {
      discount: this.discount.amount,
      discount_reason: this.discount.reason,
      discount_type: this.discount.type,
    };
  }

  toShippingParams() {
    return {
      shipping: this.shipping.type,
      shipping_price: this.shipping.amount,
      shipping_title: this.shipping.name,
    };
  }
}

export default class CreateOrderModal extends Component {
  @service appService;
  @service notificationsService;

  @tracked order;
  @tracked lastDraftOrderReponse;

  @tracked searchSuggestions;
  @tracked productSearchQuery;

  @tracked discountTypeInputValue;
  @tracked discountReasonInputValue;
  @tracked discountAmountInputValue;
  @tracked shippingRateTypeInputValue;
  @tracked customShippingRateNameInputValue;
  @tracked customShippingRateAmountInputValue;
  @tracked note;

  constructor() {
    super(...arguments);
    this.resetAllModalData();
  }

  @action
  resetSearch() {
    this.searchSuggestions = [];
  }

  @action
  resetAllModalData() {
    this.searchSuggestions = [];
    this.order = new Order();
    this.productSearchQuery = '';
    this.lastDraftOrderReponse = undefined;

    this.discountTypeInputValue = 'fixed_amount';
    this.discountReasonInputValue = '';
    this.discountAmountInputValue = undefined;

    this.shippingRateTypeInputValue = 'free';
    this.customShippingRateNameInputValue = '';
    this.customShippingRateAmountInputValue = undefined;

    this.note = '';

    this.searchShopifyForProductTask.cancelAll();
    this.createOrUpdateDraftOrderTask.cancelAll();
  }

  @action
  onSearchStringChanged(searchTerm) {
    this.productSearchQuery = searchTerm;

    if (this.productSearchQuery && this.productSearchQuery.length >= 2) {
      this.searchShopifyForProductTask.perform();
    } else {
      this.searchSuggestions = [];
    }
  }

  @action
  selectItem(searchSuggestion) {
    this.order.onSearchSuggestionSelected(searchSuggestion);
    this.onOrderUpdated();
  }

  @action
  removeLineItem(index) {
    this.order.lineItems.splice(index, 1);
    this.order.lineItems = this.order.lineItems;
    this.onOrderUpdated();
  }

  @action
  applyDiscount() {
    this.order.discount = new OrderDiscount(
      this.discountAmountInputValue,
      this.discountTypeInputValue,
      this.discountReasonInputValue,
      this.args.currencySymbol,
    );
    this.onOrderUpdated();
  }

  @action
  removeDiscount() {
    this.order.discount = null;
    this.onOrderUpdated();
  }

  @action
  applyShipping() {
    this.order.shipping = new OrderShipping(
      this.shippingRateTypeInputValue,
      this.customShippingRateAmountInputValue,
      this.customShippingRateNameInputValue,
      this.args.currencySymbol,
    );
    this.onOrderUpdated();
  }

  @action
  removeShipping() {
    this.order.shipping = null;
    this.onOrderUpdated();
  }

  @action
  onOrderUpdated() {
    this.createOrUpdateDraftOrderTask.perform();
  }

  @task({ restartable: true })
  *searchShopifyForProductTask() {
    if (!this.productSearchQuery) {
      return;
    }

    try {
      let productResponse = yield ajax({
        url: '/ember/inbox/shopify/search_products',
        type: 'GET',
        data: {
          app_id: this.appService.app.id,
          query: this.productSearchQuery,
          shopify_domain: this.args.shopifyDomain,
        },
      });

      this.searchSuggestions = productResponse.products.map((productHash) =>
        SearchSuggestion.fromProduct(productHash),
      );
    } catch (error) {
      console.error(error);
    }
  }

  @task({ restartable: true })
  *createOrUpdateDraftOrderTask() {
    try {
      let endpoint = '/ember/inbox/shopify/create_draft_order';

      let params = {
        app_id: this.appService.app.id,
        customer_id: this.args.shopifyCustomerId,
        line_items: this.order.toLineItemsParams(),
        paid: true,
        shipping: 'free',
        shopify_domain: this.args.shopifyDomain,
      };

      if (this.lastDraftOrderReponse) {
        params['draft_order_id'] = this.lastDraftOrderReponse.id;
        endpoint = '/ember/inbox/shopify/update_draft_order';
      }

      if (this.order.discount) {
        Object.assign(params, this.order.toDiscountParams());
      }

      if (this.order.shipping) {
        Object.assign(params, this.order.toShippingParams());
      }

      if (this.note) {
        params['note'] = this.note;
      }

      this.lastDraftOrderReponse = yield ajax({
        url: endpoint,
        type: 'POST',
        data: JSON.stringify(params),
      });
    } catch (error) {
      console.error(error);
    }
  }

  @task({ restartable: true })
  *saveDraftOrderTask() {
    try {
      yield this.notificationsService.notifyConfirmationWithModelAndComponent(
        {
          url: this.lastDraftOrderReponse.url,
        },
        ShopifySavedDraftOrder,
        ENV.APP.default_notification_timeout,
      );
      this.args.onModalClose();
      this.resetAllModalData();
    } catch (error) {
      this.notificationsService.notifyError(`Error: ${error}`);
      console.error(error);
    }
  }

  @task({ restartable: true })
  *completeDraftOrderTask(asPaid) {
    try {
      let params = {
        app_id: this.appService.app.id,
        draft_order_id: this.lastDraftOrderReponse.id,
        paid: asPaid,
        shopify_domain: this.args.shopifyDomain,
      };

      yield ajax({
        url: '/ember/inbox/shopify/complete_draft_order',
        type: 'POST',
        data: JSON.stringify(params),
      });

      yield this.notificationsService.notifyConfirmation('Order created');
      this.args.onModalClose();
      this.args.fetchShopifyDataTask.perform();
      this.resetAllModalData();
    } catch (error) {
      this.notificationsService.notifyError(`Error: ${error}`);
      console.error(error);
    }
  }
}
